import React, {ReactElement, useContext} from 'react';
import {useIntl, FormattedMessage} from 'react-intl';
import DeviceContext, {Device} from '../../../../RootProvider/DeviceContext';
import {useIdentityContext} from '../../../../RootProvider/IdentityContext';
import {useAllPagesData} from '../../../../utils/routes/phoenixPaths';
import {removeTrailingSlash} from '../../../../utils/routes/formatting';
import CurrentUser from '../../../Identity/CurrentUser/CurrentUser';
import Button, {ButtonVariant} from '../../Buttons/Button/Button';
import {LinkProp} from '../../../../types';
import CurrentUserMenu from '../../../Identity/CurrentUser/CurrentUserMenu';
import Dropdown from '../../Dropdown/Dropdown';
import DynamicLink from '../../../DynamicLink/DynamicLink';
import FundingOptionsLogo from '../../FundingOptionsLogo/FundingOptionsLogo';
import {isBrowser} from '../../../../browserSupport';
import {useLocale} from '../../../../RootProvider/LocaleContext';
import {getPageRoute} from '../../../../utils/routes/getPageRoute';
import Paragraph from '../../Text/Paragraph/Paragraph';

import './HeaderMainBar.scss';

export interface Props {
  links: LinkProp[];
  toggleMenu: () => void;
  showLinks?: boolean;
  focusMode?: boolean;
}

function HeaderMainBar({links, toggleMenu, showLinks, focusMode}: Props): ReactElement {
  const {formatMessage} = useIntl();
  const locale = useLocale();
  const device = useContext(DeviceContext);
  const isDesktop = device === Device.DESKTOP;
  const {isLoggedIn} = useIdentityContext();
  const pagesData = useAllPagesData();

  const currentPath = removeTrailingSlash((isBrowser && window.location.pathname) || '');
  const currentPageContext = pagesData.find(({path}) => currentPath === removeTrailingSlash(path))?.context;
  const {isAuthManagementPage} = currentPageContext || {};

  return (
    <div className="headerMainBar">
      <div className="headerMainBar__container">
        <div className="headerMainBar__logo-container">
          <DynamicLink href={getPageRoute('home', locale)}>
            <FundingOptionsLogo className="headerMainBar__logo" />
          </DynamicLink>
        </div>
        {showLinks && !focusMode && isDesktop && (
          <div className="headerMainBar__links">
            {links.map((entry: LinkProp) => {
              return (
                <DynamicLink
                  href={entry.href}
                  className="col-xs-narrow headerMainBar__link unstyledLink"
                  key={entry.label}
                >
                  {entry.label}
                </DynamicLink>
              );
            })}
          </div>
        )}
        <div className="headerMainBar__right">
          {showLinks && !focusMode && (
            <Button
              ariaLabel={formatMessage({id: 'phoenix.header.logo.menuButton'})}
              type="button"
              className="headerMainBar__menuButton"
              onClick={toggleMenu}
              variant={ButtonVariant.DEFAULT_OUTLINE}
            >
              {formatMessage({id: 'phoenix.header.logo.menuButton'})}
            </Button>
          )}

          {isLoggedIn && (
            <Dropdown
              hideCaret={!focusMode}
              hideCaretMobile
              buttonContent={<CurrentUser hideName={!focusMode || device === Device.MOBILE} />}
              className="headerMainBar__user"
            >
              <CurrentUserMenu />
            </Dropdown>
          )}

          {!isLoggedIn &&
            focusMode &&
            (isAuthManagementPage ? (
              <div className="headerMainBar__create">
                <Paragraph className="headerMainBar__createMessage">
                  <FormattedMessage id="phoenix.header.create.noAccount" />
                </Paragraph>
                <Button
                  href={formatMessage({id: 'phoenix.urls.account.create.form'})}
                  variant={ButtonVariant.PRIMARY_OUTLINE}
                  ariaLabel={formatMessage({id: 'phoenix.header.create.buttonText'})}
                >
                  <FormattedMessage id="phoenix.header.create.buttonText" />
                </Button>
              </div>
            ) : (
              <div className="headerMainBar__login">
                <Paragraph className="headerMainBar__loginMessage">
                  <FormattedMessage id="phoenix.header.login.haveAccount" />
                </Paragraph>
                <Button
                  href={formatMessage({id: 'phoenix.urls.account.login'})}
                  variant={ButtonVariant.CALL_TO_ACTION_OUTLINE}
                  ariaLabel={formatMessage({id: 'phoenix.header.login.buttonText'})}
                >
                  <FormattedMessage id="phoenix.header.login.buttonText" />
                </Button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default HeaderMainBar;
